import { defineStore } from 'pinia'
import quotationService from '@/services/quotation.service'
import type {
  ConvertToContractPayload,
  Quotation,
  QuotationFilters,
} from '@/types/quotation.types'
import type { PaginationMeta, SortOrder } from '@/types/api.types'

interface QuotationsState {
  quotations: Quotation[]
  selectedQuotation: Quotation | null
  loading: boolean
  filters: QuotationFilters
  pagination: PaginationMeta
}

export const useQuotationsStore = defineStore('quotations', {
  state: (): QuotationsState => ({
    quotations: [],
    selectedQuotation: null,
    loading: false,
    filters: { sort_order: 'desc' },
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1
    }
  }),

  getters: {
    getQuotationById: (state) => (id: string) => {
      return state.quotations.find(q => q.id === id)
    },

    pendingQuotations: (state) => {
      return state.quotations.filter(q => q.status === 'draft' || q.status === 'valid')
    },

    validQuotations: (state) => {
      return state.quotations.filter(q => q.status === 'valid')
    }
  },

  actions: {
    async fetchQuotations(page: number = 1) {
      this.loading = true
      try {
        const response = await quotationService.getQuotations({
          ...this.filters,
          page,
          page_size: this.pagination.per_page
        })
        this.quotations = response.data
        this.pagination = response.meta
      } finally {
        this.loading = false
      }
    },

    async fetchQuotation(id: string) {
      this.loading = true
      try {
        this.selectedQuotation = await quotationService.getQuotation(id)
      } finally {
        this.loading = false
      }
    },

    async createQuotation(data: any) {
      const quotation = await quotationService.createQuotation(data)
      this.quotations.unshift(quotation)
      return quotation
    },

    async updateQuotation(id: string, data: any) {
      const quotation = await quotationService.updateQuotation(id, data)
      const index = this.quotations.findIndex(q => q.id === id)
      if (index !== -1) {
        this.quotations[index] = quotation
      }
      if (this.selectedQuotation?.id === id) {
        this.selectedQuotation = quotation
      }
      return quotation
    },

    async deleteQuotation(id: string) {
      await quotationService.deleteQuotation(id)
      this.quotations = this.quotations.filter(q => q.id !== id)
      if (this.selectedQuotation?.id === id) {
        this.selectedQuotation = null
      }
    },

    async cloneQuotation(id: string) {
      const quotation = await quotationService.cloneQuotation(id)
      this.quotations.unshift(quotation)
      return quotation
    },

    async convertToContract(id: string, payload?: ConvertToContractPayload) {
      return await quotationService.convertToContract(id, payload)
    },

    async updateStatus(id: string, status: string) {
      const quotation = await quotationService.updateStatus(id, status)
      const index = this.quotations.findIndex(q => q.id === id)
      if (index !== -1) {
        this.quotations[index] = quotation
      }
      if (this.selectedQuotation?.id === id) {
        this.selectedQuotation = quotation
      }
      return quotation
    },

    setFilters(filters: QuotationFilters) {
      this.filters = filters
    },

    clearFilters() {
      this.filters = {}
    },

    setSort(sortBy: string, sortOrder: SortOrder) {
      this.filters.sort_by = sortBy
      this.filters.sort_order = sortOrder
      this.pagination.current_page = 1
    }
  }
})
